import { Box } from '@cowbell/ui/core';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './ResizableSidebar.module.css';

export interface ResizableSidebarProps {
  readonly isOpen: boolean;
  /** Keep the sidebar on screen after `isOpen` goes false and slide it out.
   *  The width is withdrawn at once, so the page widens while it leaves.
   *  `onClosed` fires when the slide ends; the host then stops passing this. */
  readonly isClosing?: boolean;
  readonly onClosed?: () => void;
  readonly children: ReactNode;
  /** Initial width in pixels. Overridden by a persisted value when
   *  `storageKey` is provided and a valid number is stored. */
  readonly defaultWidth?: number;
  readonly minWidth?: number;
  readonly maxWidth?: number;
  /** localStorage key used to persist the chosen width across reloads.
   *  Omit to keep width in-memory only. */
  readonly storageKey?: string;
  /** Name of the CSS custom property set on the document root while open.
   *  Hosts read this to reflow the surrounding layout (V1 used
   *  `--ask-moo-sidebar-width`). Defaults to that name for parity. */
  readonly cssVarName?: string;
  /** Optional callback fired when the user finishes a resize gesture
   *  (mouseup). Use for analytics or external persistence; the component
   *  already handles localStorage internally when `storageKey` is set. */
  readonly onResizeCommit?: (widthPx: number) => void;
  /** Accessible label for the aside landmark. */
  readonly ariaLabel?: string;
}

const DEFAULT_WIDTH = 420;
const DEFAULT_MIN = 320;
const DEFAULT_MAX = 800;
/** Published on `<html>` while the sidebar is open; hosts read it to reflow
 *  their layout and to tell whether the sidebar is showing. */
export const MOO_SIDEBAR_WIDTH_CSS_VAR = '--ask-moo-sidebar-width';
/** Set on `<body>` during a drag resize, so hosts can suspend width
 *  transitions that would otherwise lag behind the cursor. */
export const MOO_SIDEBAR_RESIZING_CLASS = 'moo-sidebar-resizing';

function readPersistedWidth(
  key: string | undefined,
  fallback: number,
  min: number,
  max: number
): number {
  if (!key || typeof globalThis.window === 'undefined') return fallback;
  try {
    const raw = globalThis.localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= min && parsed <= max) {
      return parsed;
    }
  } catch {
    /* localStorage unavailable — fall through */
  }
  return fallback;
}

/** Parses a computed CSS time (`0.3s`, `300ms`, or a comma list) to ms. */
function parseCssTimeMs(value: string): number {
  const first = value.split(',')[0]?.trim() ?? '';
  const n = Number.parseFloat(first);
  if (!Number.isFinite(n)) return 0;
  return first.endsWith('ms') ? n : n * 1000;
}

/** Off-screen before the first frame of an open and while closing; the
 *  transition on `.sidebar` animates between the two positions. */
function sidebarClassName(closing: boolean, hasEntered: boolean): string {
  const classes = [styles.sidebar];
  if (closing || !hasEntered) classes.push(styles.offscreen);
  if (closing) classes.push(styles.closing);
  return classes.filter(Boolean).join(' ');
}

function persistWidth(key: string | undefined, value: number): void {
  if (!key || typeof globalThis.window === 'undefined') return;
  try {
    globalThis.localStorage.setItem(key, String(Math.round(value)));
  } catch {
    /* localStorage unavailable — silently skip */
  }
}

/**
 * Fixed right-edge sidebar with a draggable left handle. Width state is
 * owned here so any host (V2 integration, future surfaces) gets the same
 * resize behavior for free.
 *
 * Behavior matches V1's `AskMooIntegration` sidebar: 320–800px clamp,
 * `ew-resize` cursor on hover, blue accent on the handle while hovered or
 * dragging, body-level cursor + select lock during drag, and
 * `--ask-moo-sidebar-width` set on `<html>` so the page layout reflows.
 */
export function ResizableSidebar({
  isOpen,
  isClosing = false,
  onClosed,
  children,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = DEFAULT_MIN,
  maxWidth = DEFAULT_MAX,
  storageKey,
  cssVarName = MOO_SIDEBAR_WIDTH_CSS_VAR,
  onResizeCommit,
  ariaLabel = 'Moo chat sidebar',
}: ResizableSidebarProps) {
  const [width, setWidth] = useState<number>(() =>
    readPersistedWidth(storageKey, defaultWidth, minWidth, maxWidth)
  );
  const [isResizing, setIsResizing] = useState(false);
  const asideRef = useRef<HTMLElement | null>(null);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  // Mount off-screen, then move on-screen before the first paint. Reading the
  // layout in between makes the browser commit the off-screen position, so
  // the move transitions from it. Doing this before paint, rather than a frame
  // later, starts the slide on the same frame the page begins to narrow.
  const [hasEntered, setHasEntered] = useState(false);
  if (!isOpen && !isClosing && hasEntered) setHasEntered(false);
  useLayoutEffect(() => {
    if (!isOpen || hasEntered) return;
    asideRef.current?.getBoundingClientRect();
    setHasEntered(true);
  }, [isOpen, hasEntered]);

  // Finish the close once the slide-out has had its full duration. Timed from
  // the computed transition rather than `transitionend`, which never fires when
  // nothing transitions (reduced motion, jsdom) and would leave the sidebar
  // stranded on screen.
  useEffect(() => {
    if (!isOpen && isClosing) {
      const el = asideRef.current;
      const ms = el
        ? parseCssTimeMs(getComputedStyle(el).transitionDuration)
        : 0;
      const timer = setTimeout(() => onClosedRef.current?.(), ms);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, isClosing]);

  // Reserve sidebar width on the document root so the surrounding layout
  // can reflow (matches V1's --ask-moo-sidebar-width contract).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (isOpen) {
      root.style.setProperty(cssVarName, `${width}px`);
    } else {
      root.style.removeProperty(cssVarName);
    }
    return () => {
      root.style.removeProperty(cssVarName);
    };
  }, [isOpen, width, cssVarName]);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setIsResizing(true);
      if (typeof document !== 'undefined') {
        document.body.classList.add(MOO_SIDEBAR_RESIZING_CLASS);
      }

      const onMove = (moveEvent: MouseEvent) => {
        // Sidebar is anchored to the right edge, so the new width is
        // viewportWidth - cursorX. Clamp into [min, max].
        const next = Math.max(
          minWidth,
          Math.min(maxWidth, globalThis.innerWidth - moveEvent.clientX)
        );
        setWidth(next);
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (typeof document !== 'undefined') {
          document.body.classList.remove(MOO_SIDEBAR_RESIZING_CLASS);
        }
        setIsResizing(false);
        setWidth((current) => {
          persistWidth(storageKey, current);
          onResizeCommit?.(current);
          return current;
        });
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [minWidth, maxWidth, storageKey, onResizeCommit]
  );

  const closing = !isOpen && isClosing;
  if ((!isOpen && !closing) || typeof document === 'undefined') return null;

  return createPortal(
    <aside
      ref={asideRef}
      aria-label={ariaLabel}
      aria-hidden={closing || undefined}
      className={sidebarClassName(closing, hasEntered)}
      style={{ width }}
    >
      <Box
        role="separator"
        aria-label="Resize sidebar"
        aria-orientation="vertical"
        className={styles.handle}
        data-active={isResizing || undefined}
        onMouseDown={handleResizeStart}
      />
      {children}
    </aside>,
    document.body
  );
}
